import React from "react";
import { Link } from "react-router-dom";
import useSeo from "@/hooks/useSeo";
import { ArrowLeft, ArrowUpRight, BookOpen, Rss, Search, X } from "lucide-react";
import { BLOG_POSTS, getHeroVisual } from "@/data/blogPosts";
import PostHeroVisual from "@/components/PostHeroVisual";

const DASABO_LOGO = "/logo.png";

// ---- JSON-LD Blog schema -----------------------------------------------

function useBlogJsonLd() {
  React.useEffect(() => {
    const id = "jsonld-blog-index";
    document.getElementById(id)?.remove();
    const s = document.createElement("script");
    s.type = "application/ld+json";
    s.id = id;
    s.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "Blog Autofattura by Dasabo",
      description:
        "Guide fiscali su autofatture estere, TD17/TD18/TD19, regime forfettario e SDI per freelance e commercialisti italiani.",
      url: "https://autofattura.dasabo.com/blog",
      inLanguage: "it",
      publisher: { "@type": "Organization", name: "DASABO SRL" },
      blogPost: BLOG_POSTS.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        description: p.description,
        url: `https://autofattura.dasabo.com/blog/${p.slug}`,
        datePublished: p.publishedAt,
        dateModified: p.updatedAt || p.publishedAt,
        author: { "@type": "Organization", name: "DASABO SRL" },
      })),
    });
    document.head.appendChild(s);
    return () => document.getElementById(id)?.remove();
  }, []);
}

// ---- Category filter helper --------------------------------------------

function useCategories() {
  return React.useMemo(() => {
    const map = new Map();
    BLOG_POSTS.forEach((p) => map.set(p.category, (map.get(p.category) || 0) + 1));
    return [{ label: "Tutti", value: "all", count: BLOG_POSTS.length }, ...[...map.entries()].map(([label, count]) => ({ label, value: label, count }))];
  }, []);
}

// ---- Main component ----------------------------------------------------

export default function BlogIndex() {
  useSeo({
    title: "Blog · Guide fiscali autofatture estere",
    description:
      "Guide, tutorial e best practice sulle autofatture estere per freelance e commercialisti italiani: TD17, TD18, TD19, reverse charge, regime forfettario, SDI e integrazioni.",
    path: "/blog",
  });
  useBlogJsonLd();

  const [activeCat, setActiveCat] = React.useState("all");
  const [query, setQuery] = React.useState("");

  const posts = React.useMemo(() => {
    const sorted = [...BLOG_POSTS].sort((a, b) => (a.publishedAt < b.publishedAt ? 1 : -1));
    const byCat = activeCat === "all" ? sorted : sorted.filter((p) => p.category === activeCat);
    if (!query.trim()) return byCat;
    const q = query.toLowerCase();
    return byCat.filter((p) =>
      p.title.toLowerCase().includes(q) ||
      p.description.toLowerCase().includes(q) ||
      (p.keywords || []).some((k) => k.toLowerCase().includes(q)),
    );
  }, [activeCat, query]);

  const categories = useCategories();
  const featured = posts[0];
  const rest = posts.slice(1);

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="blog-index-page">
      {/* Top nav */}
      <header className="sticky top-0 z-30 bg-[#FAF9F6]/85 backdrop-blur-md border-b border-[#E8E4D9]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-sm font-semibold text-[#0A0A0A] hover:text-[#1D4ED8] transition-colors" data-testid="blog-back-link">
            <ArrowLeft className="w-4 h-4" /> Torna al sito
          </Link>
          <div className="flex items-center gap-6 text-sm font-medium text-[#4A4A4A]">
            <a
              href="/rss.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="hidden sm:inline-flex items-center gap-1.5 text-[#F97316] hover:text-[#0A0A0A] transition-colors"
              data-testid="blog-rss-nav-link"
              aria-label="Feed RSS del blog"
            >
              <Rss className="w-4 h-4" />
              <span className="text-xs font-semibold uppercase tracking-wider">RSS</span>
            </a>
            <Link to="/docs" className="hover:text-[#0A0A0A] hidden sm:inline">Documentazione</Link>
            <Link to="/login" className="hover:text-[#0A0A0A] hidden sm:inline">Accedi</Link>
            <Link to="/register" className="px-4 py-1.5 rounded-full bg-[#0A0A0A] text-white hover:bg-[#1D4ED8] transition-colors">Prova gratis</Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-8 lg:pt-20 lg:pb-10">
        <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#1D4ED8] font-bold mb-4">
          <BookOpen className="w-3.5 h-3.5" />
          <span>Guide fiscali · Blog</span>
        </div>
        <h1 className="font-display font-black text-4xl sm:text-5xl lg:text-6xl tracking-tighter text-[#0A0A0A] max-w-4xl">
          Il manuale non ufficiale <span className="text-[#1D4ED8]">dell&apos;autofattura estera</span>.
        </h1>
        <p className="mt-5 text-lg text-[#4A4A4A] max-w-2xl leading-relaxed">
          Guide operative su TD17, TD18, TD19, regime forfettario, SDI e reverse charge. Ogni articolo è pensato per essere leggibile in 5 minuti e implementabile in 30 secondi. Scritto in italiano, senza fuffa.
        </p>
      </section>

      {/* Filter + search bar */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div className="flex items-center gap-2 flex-wrap" data-testid="blog-category-filter">
            {categories.map((cat) => {
              const active = activeCat === cat.value;
              return (
                <button
                  key={cat.value}
                  type="button"
                  data-testid={`blog-cat-${cat.value}`}
                  onClick={() => setActiveCat(cat.value)}
                  className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                    active
                      ? "bg-[#0A0A0A] text-white"
                      : "bg-white border border-[#E8E4D9] text-[#4A4A4A] hover:text-[#0A0A0A] hover:border-[#0A0A0A]"
                  }`}
                >
                  {cat.label}
                  <span className={`text-[10px] font-bold ${active ? "text-white/60" : "text-[#737373]"}`}>
                    {cat.count}
                  </span>
                </button>
              );
            })}
          </div>
          <div className="relative w-full sm:w-72">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#737373]" />
            <input
              type="search"
              data-testid="blog-search-input"
              placeholder="Cerca (es. TD17, Meta, forfettario…)"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              className="w-full pl-9 pr-9 py-2 rounded-full bg-white border border-[#E8E4D9] text-sm text-[#0A0A0A] placeholder-[#737373] focus:outline-none focus:border-[#0A0A0A]"
            />
            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                data-testid="blog-search-clear"
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#737373] hover:text-[#0A0A0A]"
                aria-label="Pulisci ricerca"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        </div>
      </section>

      {/* Empty state */}
      {posts.length === 0 && (
        <section className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 text-center" data-testid="blog-empty-state">
          <div className="text-6xl font-display font-black text-[#0A0A0A]/10 mb-4">0</div>
          <h3 className="font-display font-bold text-xl text-[#0A0A0A] mb-2">Nessun articolo trovato</h3>
          <p className="text-sm text-[#4A4A4A]">Prova a rimuovere il filtro o a cambiare parola chiave.</p>
        </section>
      )}

      {/* Featured post — full width dark card */}
      {featured && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-10">
          <Link
            to={`/blog/${featured.slug}`}
            data-testid={`blog-featured-${featured.slug}`}
            className="group grid lg:grid-cols-[1.15fr_1fr] rounded-3xl overflow-hidden bg-[#0A0A0A] text-white hover:shadow-2xl transition-all"
          >
            <div className="p-8 sm:p-10 lg:p-14 order-2 lg:order-1">
              <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.3em] text-[#F97316] font-bold mb-4">
                <span className="w-4 h-px bg-[#F97316]" /> In evidenza · {featured.category}
              </div>
              <h2 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl tracking-tighter leading-[1.05]">
                {featured.title}
              </h2>
              <p className="mt-4 text-white/70 leading-relaxed line-clamp-3">{featured.description}</p>
              <div className="mt-5 flex items-center gap-2 flex-wrap">
                {(featured.keywords || []).slice(0, 3).map((kw) => (
                  <span key={kw} className="text-[10px] uppercase tracking-wider text-white/60 border border-white/15 rounded-full px-2.5 py-0.5">
                    {kw}
                  </span>
                ))}
              </div>
              <div className="mt-6 inline-flex items-center gap-2 text-sm font-semibold text-white group-hover:text-[#F97316] transition-colors">
                Leggi la guida <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
              </div>
              <div className="mt-4 text-xs text-white/50 font-mono">{formatDate(featured.publishedAt)}</div>
            </div>
            <div className="order-1 lg:order-2 relative">
              <PostHeroVisual
                visual={getHeroVisual(featured.slug)}
                readingMinutes={featured.readingMinutes}
                aspect="aspect-[16/10] lg:aspect-auto lg:h-full"
                size="large"
              />
            </div>
          </Link>
        </section>
      )}

      {/* Post grid */}
      {rest.length > 0 && (
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {rest.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl bg-[#0A0A0A] text-white p-8 sm:p-12 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-6 relative overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-[#1D4ED8]/20 via-transparent to-[#F97316]/10" />
          <div className="relative z-10">
            <h3 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mb-3">
              Applica quello che leggi in 30 secondi.
            </h3>
            <p className="text-white/70 max-w-xl leading-relaxed">
              Autofattura by Dasabo automatizza tutto quello che descriviamo negli articoli. 5 crediti gratuiti al signup, senza carta.
            </p>
          </div>
          <Link
            to="/register"
            data-testid="blog-cta-register"
            className="relative z-10 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-[#0A0A0A] font-semibold text-sm hover:bg-[#F97316] hover:text-white transition-colors whitespace-nowrap"
          >
            Prova gratis <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A0A0A] text-[#737373] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between flex-wrap gap-4 text-xs">
          <div className="flex items-center gap-2 text-white font-display font-extrabold text-base">
            Autofattura by
            <img src={DASABO_LOGO} alt="Dasabo" className="h-5 w-auto brightness-0 invert" />
          </div>
          <div className="flex items-center gap-6">
            <a
              href="/rss.xml"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 text-[#F97316] hover:text-white transition-colors"
              data-testid="blog-rss-footer-link"
            >
              <Rss className="w-3.5 h-3.5" />
              <span className="font-semibold uppercase tracking-wider">Iscriviti al feed RSS</span>
            </a>
            <span>© 2026 DASABO SRL · P.IVA IT07407380828 · Palermo</span>
          </div>
        </div>
      </footer>
    </div>
  );
}

// ---- Card ---------------------------------------------------------------

function BlogCard({ post }) {
  return (
    <Link
      to={`/blog/${post.slug}`}
      data-testid={`blog-card-${post.slug}`}
      className="group relative bg-white rounded-2xl border border-[#E8E4D9] overflow-hidden hover:border-[#0A0A0A]/40 hover:shadow-xl hover:-translate-y-1 transition-all flex flex-col"
    >
      <PostHeroVisual visual={getHeroVisual(post.slug)} readingMinutes={post.readingMinutes} aspect="aspect-[16/9]" />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-center gap-2 flex-wrap mb-3">
          <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#1D4ED8]">
            {post.category}
          </span>
          {(post.keywords || []).slice(0, 1).map((kw) => (
            <React.Fragment key={kw}>
              <span className="text-[#E8E4D9]">·</span>
              <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#737373]">{kw}</span>
            </React.Fragment>
          ))}
        </div>
        <h3 className="font-display font-bold text-lg tracking-tight text-[#0A0A0A] mb-2 group-hover:text-[#1D4ED8] transition-colors leading-tight line-clamp-3">
          {post.title}
        </h3>
        <p className="text-sm text-[#4A4A4A] leading-relaxed line-clamp-2 mb-4 flex-1">{post.description}</p>
        <div className="pt-3 border-t border-[#E8E4D9] text-[11px] text-[#737373] flex items-center justify-between">
          <span>Aggiornato il {formatShortDate(post.updatedAt || post.publishedAt)}</span>
          <ArrowUpRight className="w-3.5 h-3.5 text-[#0A0A0A] group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
        </div>
      </div>
    </Link>
  );
}

// ---- Helpers ------------------------------------------------------------

function formatDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "long", year: "numeric" });
}

function formatShortDate(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString("it-IT", { day: "2-digit", month: "short", year: "numeric" });
}
